import {action, computed, observable, reaction, runInAction, toJS} from 'mobx';
import isEmpty from 'lodash/isEmpty';
import device from 'current-device';
import {PROJECT_ASSETS} from '../../constants/constants';
import {
    actions,
    category,
    events,
    ImageTabState,
    LessonNotificationType,
    NotificationType,
    ProjectAssetsType,
    StudioMode,
    VideoTabState
} from '../../constants/enums';
import {isGuest} from '../../utils/userUtility';
import {isMobile} from '../../utils/screenUtility';
import realtimeStore from '../../stores/RealtimeStore';
import projectStore from '../../containers/main/project/store';
import {customEvent} from '../../utils/analyticsUtil';
import {getAssestTypeName} from '../../utils/commonUtility';
import {buildQueryParams, getQueryParams} from '../../utils/navigationUtility';


class WizardStore {
    @observable studioMode = StudioMode.Edit;
    @observable videoTabState = VideoTabState.Initial;
    @observable imageTabState = ImageTabState.Initial;
    @observable welcomeAssets = [];
    @observable steps = [];
    @observable activeStepIndex = 0;
    @observable showLoader = false;
    @observable playerMinimizedState = false;
    @observable isPlayerMinimizedStateInitialized = false;
    @observable performVideoEnable = false;
    @observable showProjectSkills = false;
    @observable fullScreenModeByRotate = true;
    @observable fullScreenMode = device.orientation === 'landscape' && isMobile();
    @observable showStudioPanels = true
    @observable presentMode = false;
    @observable presented = false;
    @observable mixerState;
    @observable suppressWandOpen = false;
    @observable activeAction = null;


    activeThumbsRef = {};
    showListner = {};
    indexListner = {};

    preventAutorun = false;

    constructor() {
        this.initWelcomeAssets();
        this.initMixerState();

        device.onChangeOrientation(action((newOrientation) => {
            const mobileLandscape = newOrientation === 'landscape' && isMobile();
            const mobilePortrait = newOrientation === 'portrait' && isMobile();

            if (this.fullScreenModeByRotate) {
                this.fullScreenMode = mobileLandscape;
            }

            if (mobilePortrait && !this.fullScreenMode) {
                this.fullScreenModeByRotate = true;
            }
        }));
        this.listner1 = reaction(() => this.activeStepIndex, this.sendNavigationUpdate);
        this.listner2 = reaction(() => this.presentMode, this.sendNavigationUpdate);
    }

    @action initMixerState = () => {
        this.mixerState = this.steps?.map((step) => {
            if (step.type === ProjectAssetsType.Audio) {
                return !isMobile();
            }
            return false;
        });
    }

    @action togglePresentMode = async (fromUi = false) => {
        if (this.presentModeSetFromUi && !fromUi) {
            console.log('override presentMode');
            return;
        }
        this.presentMode = !this.presentMode;
        if (!this.presentMode && fromUi) {
            this.presentModeSetFromUi = true;
        }
        await wizardStore.updatePresented();
    };

    updatePresented = async () => {
        await realtimeStore.send(`project:${projectStore.activeProjectID}`, {
            type: LessonNotificationType.Presenting,
            objectId: projectStore.activeProjectID,
            objectType: NotificationType.Lesson,
            presentMode: this.presentMode
        });
    };

    @action setPresented = (val) => {
        this.presented = val;
    }


    sendNavigationUpdate = async () => {
        if ((this.presentMode || this.presented) && !this.preventAutorun) {
            let thumbs = {};
            if (this.activeThumbsRef[this.activeStepIndex]) {
                thumbs = {
                    showThumbs: this.activeThumbsRef[this.activeStepIndex].showThumbs,
                    index: this.activeThumbsRef[this.activeStepIndex].activePreviewIndex
                };
            }
            const steps = toJS(this.steps).map((step) => {
                delete step.icon;
                delete step.mediaIcon;
                return step;
            });

            await realtimeStore.send(`project:${projectStore.activeProjectID}`, {
                type: LessonNotificationType.Navigation,
                objectId: projectStore.activeProjectID,
                objectType: NotificationType.Lesson,
                navigation: {steps, activeStepIndex: this.activeStepIndex, stepType: this.currentStepType, mixerState: this.mixerState, thumbs}
            });
        }
    }

    @computed
    get getActiveThumbs() {
        return this.activeThumbsRef[this.activeStepIndex] || {};
    }

    @action
    setActiveThumbsRef(thumbsRef) {
        this.activeThumbsRef[this.activeStepIndex] = thumbsRef;
        if (this.showListner[this.activeStepIndex]) {
            this.showListner[this.activeStepIndex]();
        }
        if (this.indexListner[this.activeStepIndex]) {
            this.indexListner[this.activeStepIndex]();
        }
        this.showListner[this.activeStepIndex] = reaction(
            () => thumbsRef.showThumbs,
            () => {
                this.sendNavigationUpdate();
            }, {fireImmediately: true}
        );
        this.indexListner[this.activeStepIndex] = reaction(
            () => thumbsRef.activePreviewIndex,
            () => {
                this.sendNavigationUpdate();
            }, {fireImmediately: true}
        );
    }

    @action updateNavigation = (navigation) => {
        this.steps = navigation.steps.map(step => PROJECT_ASSETS[step.type]);
        this.preventAutorun = true;
        let stepIndex = this.getStepIndexByType(navigation.stepType);
        if (stepIndex === -1) {
            stepIndex = this.addNewStep(this.createStep(navigation.stepType));
        }
        this.mixerState = navigation.mixerState;
        this.goToStep(stepIndex, true);

        setTimeout(() => {
            runInAction(() => {
                projectStore.notations = projectStore.notations.slice().sort(item => (item.default ? -1 : 1));
                if (this.activeThumbsRef[this.activeStepIndex] && navigation.thumbs && navigation.thumbs.index >= 0) {
                    this.activeThumbsRef[this.activeStepIndex].setShowThumbs(navigation.thumbs.showThumbs);
                    this.activeThumbsRef[this.activeStepIndex].setActivePreviewIndex(navigation.thumbs.index);
                }
                setTimeout(() => {
                    this.preventAutorun = false;
                });
            });
        }, 0);
    }

    getStepIndexByType = type => this.steps.findIndex(step => step.type === type)

    createStep = type => ({isActive: true, isEditable: false, ...PROJECT_ASSETS[type]})


    @computed
    get disabledWelcome() {
        return !this.welcomeAssets.some(item => item.isActive);
    }

    @computed
    get assetsMenu() {
        return PROJECT_ASSETS.map(item => ({...item, inActive: !this.steps.some(s => s.type === item.type)}));
    }

    @computed
    get isAllStepsFulfilled() {
        return this.assetsMenu.every(item => !item.inActive);
    }

    @action setPerformVideoEnable = (val) => {
        this.performVideoEnable = val;
    };

    @action initWelcomeAssets = () => {
        this.welcomeAssets = PROJECT_ASSETS.map(item => ({...item, isActive: true}));
    };

    @action setStudioMode = (mode) => {
        this.studioMode = mode;
    };

    @action setVideoTabState = (state) => {
        this.videoTabState = state;
    };

    @action setImageTabState = (state) => {
        this.imageTabState = state;
    };

    @action changeWelcomeAssets = (index) => {
        this.welcomeAssets[index].isActive = !this.welcomeAssets[index].isActive;
    };

    // finishWelcome = () => {
    //     this.initNewSteps();
    //     this.setStudioMode(StudioMode.Edit);
    // };

    @action initSingleStep = (index = ProjectAssetsType.Text, action) => {
        const step = PROJECT_ASSETS[index];
        step.isActive = true;
        step.isEditable = true;
        this.steps = [step];
        this.goToStep(0, true);
        this.initMixerState();
        this.setStudioMode(StudioMode.Preview);
        this.activeAction = action;
    }

    @action clearAction = () => {
        this.activeAction = null;
    }

    // @action initNewSteps = (startIndex = 0) => {
    //     this.goToStep(startIndex);
    //     this.steps = this.welcomeAssets.reduce((res, item) => {
    //         if (item.isActive) res.push(item);
    //         return res;
    //     }, []);
    //     this.initMixerState();
    // };

    isStepEmpty = (stepIndex, project) => {
        const step = this.steps[stepIndex];
        if (!step) {
            return false;
        }
        switch (step.type) {
            case ProjectAssetsType.Text:
                return !this.isDescriptionExists(project.description);
            case ProjectAssetsType.Notation:
                return isEmpty(project.notations);
            case ProjectAssetsType.Audio:
                return isEmpty(project.loopers);
            case ProjectAssetsType.Video:
                return isEmpty(project.videoUrls);
            case ProjectAssetsType.Image:
                return isEmpty(project.attachments);
            default:
                console.log('unknown step type: ', step);
                return false;
        }
    }

    @action reorderSteps = (sourceIndex, targetIndex) => {
        const steps = this.steps.slice();
        const [removed] = steps.splice(sourceIndex, 1);
        steps.splice(targetIndex, 0, removed);
        this.steps = steps;
        this.goToStep(targetIndex);
        this.sendNavigationUpdate();
    }

    @action removeStep = (stepToDelete, projectStore) => {
        const step = this.steps[stepToDelete];
        if (!step) {
            return;
        }

        switch (step.type) {
            case ProjectAssetsType.Text:
                projectStore.clearDescription();
                break;
            case ProjectAssetsType.Notation:
                projectStore.clearNotations();
                break;
            case ProjectAssetsType.Audio:
                projectStore.clearProjectLoopers();
                break;
            case ProjectAssetsType.Video:
                projectStore.clearVideos();
                break;
            case ProjectAssetsType.Image:
                projectStore.clearImages();
                break;
            default:
                console.log('unknown step type: ', step);
        }
        const newSteps = this.steps.slice();
        newSteps.splice(stepToDelete, 1);
        this.steps = newSteps;
        this.goToStep(Math.max(stepToDelete - 1, 0));
    }

    @action initProjectSteps = (project, startIndex = 0) => {
        this.setStudioMode(StudioMode.Preview);
        const projectAssets = [];
        if (project.steps && project.steps.length > 0) {
            // console.log('using project steps:', project.steps)
            project.steps.map((stepType) => {
                projectAssets.push(PROJECT_ASSETS[stepType]);
            });
        } else {
            // console.log('using default steps fallback');
            if (this.isDescriptionExists(project.description)) {
                projectAssets.push(PROJECT_ASSETS[0]);
            }
            if (!isEmpty(project.notations)) {
                projectAssets.push(PROJECT_ASSETS[1]);
            }
            if (!isEmpty(project.loopers)) {
                projectAssets.push(PROJECT_ASSETS[2]);
            }
            if (!isEmpty(project.videoUrls)) {
                projectAssets.push(PROJECT_ASSETS[3]);
            }
            if (!isEmpty(project.attachments)) {
                projectAssets.push(PROJECT_ASSETS[4]);
            }
        }
        if (!this.areStepsArraysEqual(projectAssets, this.steps)) {
            this.steps = projectAssets;
            this.initMixerState();
            if (projectAssets.length > 0) {
                const index = Math.min(Number(startIndex), this.steps.length - 1);
                this.goToStep(index);
            }
        }
        if (isGuest()) {
            this.steps.forEach((p) => {
                p.isEditable = true;
            });
        }
    };

    @action toggleStepEditable = (type) => {
        const step = this.steps.find(s => s.type === type);
        step.isEditable = !step.isEditable;
    };

    @action changeStepEditable = (type, val) => {
        const step = this.steps.find(s => s.type === type);
        step.isEditable = val;
    };

    @computed get currentSteps() {
        return this.steps.slice();
    }

    @computed
    get currentStepType() {
        return this.currentStep?.type;
    }

    @computed
    get currentStep() {
        const currentStep = this.steps[this.activeStepIndex];
        if (currentStep) {
            return currentStep;
        }
        return null;
    }

    closeAllStepEditable = () => {
        this.steps.forEach((step) => {
            runInAction(() => {
                step.isEditable = false;
            });
        });
    };

    areStepsArraysEqual = (arr1, arr2) => {
        if (arr1.length !== arr2.length) {
            return false;
        }
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i].type !== arr2[i].type) {
                return false;
            }
        }
        return true;
    }

    isDescriptionExists = descriptionStr => !(isEmpty(descriptionStr) || descriptionStr === '<p></p>' || descriptionStr === '<p><br></p>')

    @action addStepByType = (type, action) => {
        let stepIndex = this.getStepIndexByType(type);
        if (stepIndex === -1) {
            stepIndex = this.addNewStep(PROJECT_ASSETS[type]);
        }
        if (this.activeStepIndex !== stepIndex) {
            this.goToStep(stepIndex, true);
        }
        setTimeout(() => {
            runInAction(() => {
                this.activeAction = action;
            });
        });
    }

    @action addNewStep = (step) => {
        this.steps.push(step);
        this.mixerState.push(step.type === ProjectAssetsType.Audio ? !isMobile() : false);
        return this.steps.length - 1;
    };

    @action goToStep = (index, suppressWandOpen = false) => {
        this.activeStepIndex = Math.max(0, index);
        this.suppressWandOpen = suppressWandOpen;
        this.setTabIndexInUrl(this.activeStepIndex);
        try {
            customEvent(`${PROJECT_ASSETS[this.currentStepType].label}${events.StudioPageView}`);
        } catch (err) {
        }
        if (!isGuest()) {
            this.closeAllStepEditable();
        }
        const thumbsPanelRef = this.activeThumbsRef[this.activeStepIndex];
        if (thumbsPanelRef) {
            thumbsPanelRef.setShowThumbs(false);
        }
    };

    prevStep = () => {
        if (this.activeStepIndex > 0) {
            this.goToStep(this.activeStepIndex - 1);
        }
    };

    nextStep = () => {
        if (this.activeStepIndex < this.steps.length - 1) {
            this.goToStep(this.activeStepIndex + 1);
        }
    };

    get isLastStep() {
        return this.activeStepIndex === this.steps.length - 1;
    }

    get isFirstStep() {
        return this.activeStepIndex === 0;
    }

    setTabIndexInUrl = (tabIndex) => {
        const params = getQueryParams();
        params.tabIndex = tabIndex;
        window.history.replaceState({}, document.title, `${window.location.pathname}?${buildQueryParams(params)}`);
    };

    @action openLoader = () => {
        this.showLoader = true;
    };

    @action closeLoader = () => {
        this.showLoader = false;
    }

    @action reset = () => {
        this.steps = [];
        this.activeStepIndex = 0;
        this.studioMode = StudioMode.Edit;
        this.videoTabState = VideoTabState.Initial;
        this.imageTabState = ImageTabState.Initial;
        this.isPlayerMinimizedStateInitialized = false;
        this.playerMinimizedState = false;
        this.presentMode = false;
        this.presented = false;
        this.presentModeSetFromUi = false;
        this.fullScreenMode = false;
    }

    @action setGlobalPlayerMinimizedState = (val) => {
        this.isPlayerMinimizedStateInitialized = true;
        this.playerMinimizedState = val;
    }

    doneWelcome = (project = {}) => {
        if (!project.id) {
            this.openLoader();
            setTimeout(() => this.closeLoader(), 3000);
            this.initProjectSteps(project);
        }
        this.setStudioMode(StudioMode.Preview);
    }

    @action openProjectSkills = () => {
        this.showProjectSkills = true;
    };

    @action closeProjectSkills = () => {
        this.showProjectSkills = false;
    };

    @action toggleProjectSkills = () => {
        this.showProjectSkills = !this.showProjectSkills;
    }

    @action toggleFullScreenMode = () => {
        this.fullScreenMode = !this.fullScreenMode;
        const label = this.fullScreenMode ? 'open' : 'close';
        customEvent(events.CustomEvent, {category: category.FullScreen, action: actions.Click, label});
        if (this.fullScreenMode) {
            this.showStudioPanels = false;
        }
        if (device.orientation === 'portrait' && isMobile()) {
            this.fullScreenModeByRotate = !this.fullScreenModeByRotate;
        }
    };

    @action setMixerOpen(val) {
        if (this.mixerMode !== val) {
            customEvent(events.CustomEvent, {
                category: isGuest() ? category.Discover : category.Studio,
                action: actions.ToggleShowLooper,
                label: getAssestTypeName(this.currentStepType)
            });
            this.mixerState[this.activeStepIndex] = val;
            this.sendNavigationUpdate();
        }
    }

    @computed get mixerMode() {
        return this.mixerState[this.activeStepIndex] || false;
    }

    @action toggleShowStudioPanels = () => {
        if (this.fullScreenMode) {
            this.showStudioPanels = !this.showStudioPanels;
        }
    }
}

const wizardStore = new WizardStore();
export default wizardStore;
